/* Did the documentation changes help? Compares the same measures for the weeks either side
 * of each change — for the turns that retrieved the new document (targeted) and for all
 * traffic (overall) — and says so when two changes overlap and the attribution is ambiguous,
 * rendered under the upload form on the Documents page.
 *
 * A single re-index can record dozens of changes, so each one is a line in a table — the
 * overall movement, which is what a scan is looking for — and the six-measure before → after
 * breakdown belongs to whichever line is open.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { ImpactItem } from '../api/types';
import { Signal } from '../components/Signal';
import { Muted, Table } from '../components/Table';
import { type TileSpec } from '../components/Tiles';
import { num, pct, shortDate } from '../lib/format';
import { useApp } from '../state/app';

type Format = (value: number | null) => string;

const money = (v: number | null) => num(v, 2);
const count = (v: number | null) => num(v);
const rate = (v: number | null) => pct(v);

/**
 * One before → after pair on a single line. The arrow states the direction the measure
 * moved, the colour whether that was the wanted one.
 */
function Delta({
  before,
  after,
  format,
  lowerIsBetter = false,
}: {
  before: number | null;
  after: number | null;
  format: Format;
  lowerIsBetter?: boolean;
}) {
  const delta = before == null || after == null ? null : after - before;
  const moved = delta != null && Math.abs(delta) > 1e-9;
  const good = moved && (lowerIsBetter ? delta < 0 : delta > 0);

  return (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap tabular-nums">
      <span className="text-[var(--text-muted)]">{format(before)}</span>
      <span className="text-[var(--text-muted)]">→</span>
      <span className="font-medium text-[var(--text-primary)]">{format(after)}</span>
      {moved && (
        <span style={{ color: `var(${good ? '--chart-good' : '--chart-critical'})` }}>
          {delta > 0 ? '↑' : '↓'}
        </span>
      )}
    </span>
  );
}

/** The same pair with its own heading, for the opened breakdown. */
function Metric({
  label,
  before,
  after,
  format,
  lowerIsBetter = false,
}: {
  label: string;
  before: number | null;
  after: number | null;
  format: Format;
  lowerIsBetter?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{label}</div>
      <div className="mt-0.5">
        <Delta before={before} after={after} format={format} lowerIsBetter={lowerIsBetter} />
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
      <div className="mt-2 grid gap-4 sm:grid-cols-3">{children}</div>
    </div>
  );
}

/** The document a change was made to, which is the name a reader scans the table for. The
 *  ambiguity carries its full sentence in the opened row; here it is only a marker. */
function ChangeCell({ item, open }: { item: ImpactItem; open: boolean }) {
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Chevron className="h-3.5 w-3.5 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
      <span className="font-medium text-[var(--text-primary)]">{item.change.action}</span>
      {item.change.source ? (
        <code className="truncate rounded bg-[var(--bg-tertiary)] px-1.5 py-0.5 text-xs">
          {item.change.source}
        </code>
      ) : (
        <Muted>—</Muted>
      )}
      {item.other_changes_in_window > 0 && (
        <span
          className="shrink-0 text-[var(--chart-warning)]"
          title={`${num(item.other_changes_in_window)} other change(s) landed in the same window, so the attribution is ambiguous`}
        >
          <span aria-hidden="true">▲</span>
          <span className="sr-only">attribution is ambiguous</span>
        </span>
      )}
    </div>
  );
}

/** The six measures for one change, under the table, for the row that is open. */
function ChangeDetail({ item, onClose }: { item: ImpactItem; onClose: () => void }) {
  const { simple, word } = useApp();

  return (
    <div className="mt-4 rounded-[var(--radius-md)] border border-[var(--border-light)] bg-[var(--bg-elevated)] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-medium text-[var(--text-primary)]">{item.change.action}</span>
          {item.change.source && (
            <code className="rounded bg-[var(--bg-tertiary)] px-1.5 py-0.5 text-xs">
              {item.change.source}
            </code>
          )}
          <span className="text-sm text-[var(--text-muted)]">
            · {shortDate(item.change.ts)} · by {item.change.admin_user || 'unknown'}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          {word('Close', 'Hide')}
        </button>
      </div>

      {item.other_changes_in_window > 0 && (
        <div className="mt-2">
          <Signal tone="warning">
            ▲ {num(item.other_changes_in_window)} other change(s) in the same{' '}
            {word(
              'window — attribution is ambiguous',
              'fortnight, so this comparison cannot say which change did what',
            )}
          </Signal>
        </div>
      )}

      <div className="mt-4 space-y-4">
        {!simple && (
          <Section
            title={`Targeted — turns that retrieved this document (±${num(item.window_days)} days)`}
          >
            <Metric
              label="Turns"
              before={item.targeted.before.turns}
              after={item.targeted.after.turns}
              format={count}
            />
            <Metric
              label="Avg evidence"
              before={item.targeted.before.avg_confidence}
              after={item.targeted.after.avg_confidence}
              format={money}
            />
            <Metric
              label="Escalation rate"
              before={item.targeted.before.escalation_rate}
              after={item.targeted.after.escalation_rate}
              format={rate}
              lowerIsBetter
            />
          </Section>
        )}

        <Section
          title={word(
            'Overall — all traffic in the same window',
            `Everything people asked, ${num(item.window_days)} days before and after`,
          )}
        >
          {simple ? (
            <Metric
              label="Questions asked"
              before={item.overall.before.turns}
              after={item.overall.after.turns}
              format={count}
            />
          ) : (
            <Metric
              label="Avg evidence"
              before={item.overall.before.avg_confidence}
              after={item.overall.after.avg_confidence}
              format={money}
            />
          )}
          <Metric
            label={word('Escalation rate', 'Ended with a person')}
            before={item.overall.before.escalation_rate}
            after={item.overall.after.escalation_rate}
            format={rate}
            lowerIsBetter
          />
          <Metric
            label={word('Rated helpful', 'Answers people liked')}
            before={item.overall.before.helpful_rate}
            after={item.overall.after.helpful_rate}
            format={rate}
          />
        </Section>
      </div>
    </div>
  );
}

/**
 * What the documents you added have done, counted. The last tile counts changes that landed
 * in the same fortnight as another, which is the size of the doubt over the comparisons.
 */
export function impactTiles(
  items: ImpactItem[],
  word: (full: string, plain: string) => string,
): TileSpec[] {
  const latest = items.reduce<ImpactItem | null>(
    (newest, item) => (!newest || item.change.ts > newest.change.ts ? item : newest),
    null,
  );
  const reached = items.reduce((total, item) => total + Number(item.targeted.after.turns || 0), 0);
  const ambiguous = items.filter((item) => item.other_changes_in_window > 0).length;

  return [
    {
      label: word('Changes recorded', 'Documents you added'),
      value: num(items.length),
      hint: 'every upload and re-index this page can compare across',
    },
    {
      label: word('Most recent', 'Last one added'),
      value: latest ? shortDate(latest.change.ts) : '—',
      hint: latest
        ? `${latest.change.source || latest.change.action}`
        : 'nothing has been added yet',
    },
    {
      label: word('Turns that retrieved them', 'Questions answered from them'),
      value: num(reached),
      hint: 'in the fortnight after each change landed',
    },
    {
      label: word('Ambiguous attribution', 'Cannot be judged on their own'),
      value: num(ambiguous),
      hint: ambiguous
        ? 'another change landed in the same fortnight, so the comparison cannot say which did what'
        : 'each change had its fortnight to itself',
    },
  ];
}

/** One line per change, newest first, with the opened one's full breakdown beneath the
 *  table — or the sentence that says why there are none. */
export function ImpactList({ items }: { items: ImpactItem[] }) {
  const { word } = useApp();
  const [openId, setOpenId] = useState<number | null>(null);
  const open = items.find((item) => item.change.id === openId) ?? null;

  if (items.length === 0) {
    return (
      <p className="max-w-prose py-6 text-sm text-[var(--text-muted)]">
        {word(
          'No changes recorded yet. Upload a document above and this fills in.',
          'Nothing added yet. Use the form above, then look back in a week.',
        )}
      </p>
    );
  }

  const toggle = (item: ImpactItem) =>
    setOpenId((current) => (current === item.change.id ? null : item.change.id));

  return (
    <>
      <Table
        rows={items}
        pageSize={10}
        search={word('Search by document or who changed it', 'Search by document')}
        empty="No change recorded in this window."
        columns={[
          {
            id: 'change',
            label: 'Change',
            simple: 'Document',
            render: (item) => <ChangeCell item={item} open={item.change.id === openId} />,
            sortValue: (item) => item.change.source || item.change.action,
          },
          {
            id: 'ts',
            label: 'When',
            render: (item) => shortDate(item.change.ts),
            sortValue: (item) => item.change.ts,
          },
          {
            id: 'admin_user',
            label: 'By',
            advanced: true,
            render: (item) => item.change.admin_user || <Muted>unknown</Muted>,
            sortValue: (item) => item.change.admin_user ?? '',
          },
          {
            id: 'targeted_turns',
            label: 'Retrieved',
            advanced: true,
            num: true,
            render: (item) => (
              <Delta
                before={item.targeted.before.turns}
                after={item.targeted.after.turns}
                format={count}
              />
            ),
            sortValue: (item) => item.targeted.after.turns ?? null,
          },
          {
            id: 'evidence',
            label: 'Evidence',
            advanced: true,
            num: true,
            render: (item) => (
              <Delta
                before={item.overall.before.avg_confidence}
                after={item.overall.after.avg_confidence}
                format={money}
              />
            ),
            sortValue: (item) => item.overall.after.avg_confidence ?? null,
          },
          {
            id: 'escalation',
            label: 'Escalation',
            simple: 'Ended with a person',
            num: true,
            render: (item) => (
              <Delta
                before={item.overall.before.escalation_rate}
                after={item.overall.after.escalation_rate}
                format={rate}
                lowerIsBetter
              />
            ),
            sortValue: (item) => item.overall.after.escalation_rate ?? null,
          },
          {
            id: 'helpful',
            label: 'Rated helpful',
            simple: 'Answers people liked',
            num: true,
            render: (item) => (
              <Delta
                before={item.overall.before.helpful_rate}
                after={item.overall.after.helpful_rate}
                format={rate}
              />
            ),
            sortValue: (item) => item.overall.after.helpful_rate ?? null,
          },
          {
            id: 'detail',
            label: '',
            render: (item) => (
              // The row's own click cannot be reached by keyboard, so this is what Tab lands
              // on. The stopPropagation keeps one click from toggling the row twice.
              <div className="text-right">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(item);
                  }}
                  aria-expanded={item.change.id === openId}
                  aria-label={`Before and after for ${item.change.source || item.change.action}`}
                  title={word('Show the before → after measures', 'Show what changed')}
                  className="rounded-[var(--radius-sm)] px-2 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
                >
                  {item.change.id === openId ? 'Hide' : word('Detail', 'Show')}
                </button>
              </div>
            ),
          },
        ]}
        onRowClick={toggle}
      />

      {open && <ChangeDetail item={open} onClose={() => setOpenId(null)} />}
    </>
  );
}
